import React, { useState } from "react";
import { useTable, List, EditButton, ShowButton, CreateButton } from "@refinedev/antd";
import { Table, Space, Tag, Input, Typography } from "antd";
import dayjs from "dayjs";
import { SearchOutlined } from "@ant-design/icons";
import { INVOICE_QUERY } from "./queries";
import { Invoice, InvoiceStatus } from "./interface";

interface IInvoiceRow extends Invoice {}


const InvoiceStatusBadge: React.FC<{ status: InvoiceStatus }> = ({ status }) => {
  const colorMap: Record<InvoiceStatus, string> = {
    [InvoiceStatus.PENDING]: "processing",
    [InvoiceStatus.MATCHED]: "blue",
    [InvoiceStatus.SUBMITTED]: "processing",
    [InvoiceStatus.APPROVED]: "success",
    [InvoiceStatus.PAID]: "success",
    [InvoiceStatus.REJECTED]: "error",
  };
  return <Tag color={colorMap[status] || "default"}>{status}</Tag>;
};

export const InvoiceList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");

  const { tableProps } = useTable<IInvoiceRow>({
    resource: "invoices",
    initialSorter: [{ field: "createdAt", order: "asc" }],
    initialPageSize: 10,
    meta: {
      gqlQuery: INVOICE_QUERY,
    },
    onSearch: (params: any) => {
      const { q } = params;
      return [
        {
          field: "invoiceNumber",
          operator: "contains",
          value: q ? q : undefined,
        },
        {
          field: "supplierName",
          operator: "contains",
          value: q ? q : undefined,
        },
        {
          field: "poNumber",
          operator: "contains",
          value: q ? q : undefined,
        },
      ];
    },
  });

  const dataSource: IInvoiceRow[] = ((tableProps as any)?.dataSource || []).filter(
    (row: IInvoiceRow) =>
      !searchTerm ||
      row.invoiceNumber?.toLowerCase?.().includes(searchTerm.toLowerCase()) ||
      row.supplierName?.toLowerCase?.().includes(searchTerm.toLowerCase()) ||
      row.poNumber?.toLowerCase?.().includes(searchTerm.toLowerCase())
  );

  return (
    <List
      headerProps={{
        extra: (
          <Space>
            <Input
              placeholder="Search by Invoice No, Supplier or PO..."
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onPressEnter={(e) => setSearchTerm(e.currentTarget.value)}
              style={{ width: 280 }}
            />
            <CreateButton />
          </Space>
        ),
      }}
    >
      <Table {...tableProps} dataSource={dataSource} rowKey="id">
        <Table.Column dataIndex="invoiceNumber" title="Invoice No" sorter />
        <Table.Column dataIndex="supplierName" title="Supplier" sorter />
        <Table.Column dataIndex="poNumber" title="PO No" sorter />
        <Table.Column
          dataIndex="totalAmount"
          title="Total Amount"
          sorter
          render={(v: number, record: IInvoiceRow) => `${record.currency} ${Number(v || 0).toLocaleString()}`}
        />
       
        <Table.Column
          dataIndex="status"
          title="Status"
          render={(status: InvoiceStatus) => <InvoiceStatusBadge status={status} />}
          sorter
        />
        <Table.Column
          dataIndex="paymentRequestId"
          title="Payment Request"
          render={(paymentRequestId: string) => 
            paymentRequestId ? (
              <Typography.Link href={`/payment-requests/show/${paymentRequestId}`}>
                {paymentRequestId}
              </Typography.Link>
            ) : (
              <Typography.Text type="secondary">-</Typography.Text>
            )
          }
        />
        <Table.Column
          dataIndex="createdAt"
          title="Created"
          render={(date: string) => dayjs(date).format("YYYY-MM-DD")}
          sorter
        />
        <Table.Column
          title="Actions"
          dataIndex="actions"
          render={(_text: string, record: IInvoiceRow) => (
            <Space>
              <EditButton size="small" recordItemId={record.id} resource="invoices" />
              <ShowButton size="small" recordItemId={record.id} resource="invoices" />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};